/**
 * <WorkerAvatar> — the worker register's NAME column (13-ui-screens.md §9.3),
 * the profile header tile (§9.4) and the day-sheet's photo preview modal
 * (§7.7). ADR-0015.
 *
 * Shows the stored photo when `photoUpdatedAt` is set, else the SAME
 * initials chip the profile header has always used — `--green-100` /
 * `--brand-primary`, 4px radius, `--font-display` at 800 weight (this is
 * deliberately NOT the top bar's user chip in `TopBar.tsx`, which is a
 * different shade for a different kind of person: the signed-in USER, not a
 * plant worker). Falls back to initials for every non-photo state — no
 * photo, still loading, or the fetch failed — because a chip that
 * occasionally flashes blank while the DTO's `photoUpdatedAt` clearly says a
 * photo exists would look broken; `useWorkerPhotoUrl` returns `null` for all
 * three, and this component does not need to tell them apart.
 *
 * Bytes are never put in an `<img src>` pointing at the API directly — the
 * bearer token lives in a module closure, not a cookie (`client.ts` §3.1),
 * so an `<img>` cannot authenticate on its own. `useWorkerPhotoUrl` fetches
 * through `useWorkerPhoto` (script, with the bearer + company header) and
 * hands back a `blob:` object URL instead.
 */
import { useEffect, useState, type CSSProperties } from 'react';
import { initialsOf } from '@/lib/auth';
import { useWorkerPhoto } from '@/api/queries';

export interface WorkerAvatarProps {
  name: string;
  workerId: string;
  /** `WorkerListItemDto.photoUpdatedAt` / `WorkerDto.photoUpdatedAt`. `null` = no photo. */
  photoUpdatedAt: string | null;
  size: number;
  /** When given, the avatar renders as a `<button>` — the day-sheet photo modal's trigger. */
  onClick?: () => void;
}

function shellStyle(size: number): CSSProperties {
  return {
    width: size,
    height: size,
    flex: `0 0 ${size}px`,
    borderRadius: 4,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--green-100)',
    color: 'var(--brand-primary)',
    fontFamily: 'var(--font-display)',
    fontWeight: 800,
    // Scales with `size` so the same chip reads right at the register's 28px
    // and the profile header's 44px, rather than hardcoding one.
    fontSize: Math.round(size * 0.34),
    lineHeight: 1,
  };
}

const IMG_STYLE: CSSProperties = { width: '100%', height: '100%', objectFit: 'cover' };

export function WorkerAvatar({ name, workerId, photoUpdatedAt, size, onClick }: WorkerAvatarProps) {
  const url = useWorkerPhotoUrl(workerId, photoUpdatedAt);
  const content = url ? (
    <img src={url} alt={name} draggable={false} style={IMG_STYLE} />
  ) : (
    initialsOf(name)
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={`View photo of ${name}`}
        style={{ ...shellStyle(size), border: 0, padding: 0, cursor: 'pointer' }}
      >
        {content}
      </button>
    );
  }
  return <div style={shellStyle(size)}>{content}</div>;
}

/**
 * The worker's photo as a `blob:` object URL, or `null` while there is no
 * photo, the fetch is still pending, or it failed.
 *
 * Exported (not just used internally by `<WorkerAvatar>`) so the day-sheet's
 * read-only photo preview modal (`WorkerPhotoPreviewModal.tsx`) can show the SAME image without a
 * second W37 request: both go through `useWorkerPhoto`'s
 * `(workerId, photoUpdatedAt)` cache key, so opening the modal right after
 * the avatar has already rendered is a cache hit and this hook only mints a
 * fresh object URL from the already-cached `Blob`.
 *
 * The URL is created and revoked in an effect, never `useMemo`: revoking
 * eagerly (e.g. inside the render or a memo cleanup that can fire before the
 * browser has painted the new `src`) produces a blank frame in some
 * browsers, the same object-URL race `payroll/-lib/download.ts` documents
 * for its own download link. The effect's cleanup revokes the URL it
 * created, on every `blob` change and on unmount, so nothing outlives the
 * component that asked for it.
 */
export function useWorkerPhotoUrl(workerId: string, photoUpdatedAt: string | null): string | null {
  const { data: blob } = useWorkerPhoto(workerId, photoUpdatedAt);
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!blob) {
      setUrl(null);
      return;
    }
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [blob]);

  return url;
}
